/**
 * /chat — RAG chat. Surface Layer (/01)
 *
 * Reuses the MCP-verified @carbon/ai-chat host. User messages are
 * dispatched as `rag.chat_request` ActionFrames through the BFF. On the
 * loopback (DevHub) transport the local-AI worker resolves the answer
 * synchronously; on the public (serverless) edge the frame is enqueue-only,
 * so the host shows a "queued" state until the execution worker writes the
 * answer to action_results (read back via GET /api/conversations). Auth +
 * PBAC are enforced server-side.
 *
 * A guardrail strip surfaces the latest answer's FoundationModels grounding
 * verdict (verified / held-for-review) — the anti-hallucination loop made visible.
 */

import { createFileRoute } from '@tanstack/react-router';
import { InlineNotification, Tag, Tile } from '@carbon/react';
import { AiChatHost } from '@/sample-carbon/ai-chat';
import { useConversations } from '@lib/persistence/useRuntimeData';
import type { ConversationResult } from '@contracts/runtime-endpoints.contract';
import '../styles/views.scss';

export const Route = createFileRoute('/chat')({
  component: ChatView,
});

function latestGuardrailResult(
  conversations: { results: ConversationResult[] }[]
): ConversationResult | null {
  for (const conversation of conversations) {
    for (const result of conversation.results) {
      if (result.is_guardrail_passed !== undefined && result.is_guardrail_passed !== null) {
        return result;
      }
    }
  }
  return null;
}

function ChatView() {
  const { conversations, tenantContextMissing } = useConversations(undefined, { pollMs: 4000 });
  const verdict = latestGuardrailResult(conversations);

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Chat</h1>
        <p className="view__subtitle">
          Authenticated RAG chat — every message is dispatched as a <code>rag.chat_request</code> ActionFrame through the BFF.
        </p>
      </header>

      {tenantContextMissing ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Workspace being provisioned"
          subtitle="Chat history appears once your workspace context is ready."
        />
      ) : null}

      {verdict ? (
        <Tile className="view-tile">
          <div className="view-tile__row">
            <span className="view-tile__label">Latest answer · guardrail</span>
            <Tag type={verdict.is_guardrail_passed ? 'green' : 'red'}>
              {verdict.is_guardrail_passed ? 'grounding verified' : 'held for review'}
            </Tag>
          </div>
          {verdict.guardrail_verdict?.reason ? (
            <p className="view-tile__label">{verdict.guardrail_verdict.reason}</p>
          ) : null}
        </Tile>
      ) : null}

      <div className="view__section">
        <AiChatHost />
      </div>
    </div>
  );
}
